import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { PropsWithChildren } from 'react';
import Box from '@mui/material/Box';
import Fade from '@mui/material/Fade';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CloudOffOutlined from '@mui/icons-material/CloudOffOutlined';
import CheckCircle from '@mui/icons-material/CheckCircle';
import { visuallyHidden } from '@mui/utils';

import {
  CONNECTION_BANNER_MESSAGES,
  type ConnectionBannerStatus,
  useConnectionBannerStatus,
} from 'app-components/hooks/connection/connectionBanner';

import { useAppSelector } from 'hooks/redux';
import { getIsOffline } from 'data/app/selectors';

// Figma heights for the page bar and for the footer that replaces dialog actions.
const BAR_MIN_HEIGHT = 70;
const FOOTER_MIN_HEIGHT = 98;

const FOOTER_SX = { position: 'sticky', bottom: 0, flexShrink: 0, zIndex: 1 } as const;

type ConnectionContextValue = {
  status: ConnectionBannerStatus | null;
  registerFooter: () => () => void;
};

const ConnectionContext = createContext<ConnectionContextValue>({ status: null, registerFooter: () => () => {} });

type ConnectionBannerProps = {
  status: ConnectionBannerStatus;
  /** `footer` fills the space of the dialog actions it replaces. */
  variant?: 'bar' | 'footer';
};

export function ConnectionBanner({ status, variant = 'bar' }: ConnectionBannerProps): React.JSX.Element {
  const reconnected = status === 'reconnected';
  return (
    <Stack
      direction='row'
      alignItems='center'
      justifyContent='center'
      gap={1}
      sx={{
        px: 3,
        py: 1,
        minHeight: variant === 'footer' ? FOOTER_MIN_HEIGHT : BAR_MIN_HEIGHT,
        boxSizing: 'border-box',
        width: '100%',
        bgcolor: reconnected ? 'success.dark' : 'icon.main',
        color: (theme) =>
          reconnected
            ? theme.palette.success.light
            : theme.palette.mode === 'dark'
              ? theme.palette.common.black
              : theme.palette.common.white,
      }}
    >
      {reconnected ? (
        <CheckCircle sx={{ fontSize: 20, flexShrink: 0 }} />
      ) : (
        <CloudOffOutlined sx={{ fontSize: 24, flexShrink: 0 }} />
      )}
      <Typography variant='paragraphMedium' textAlign='center'>
        {CONNECTION_BANNER_MESSAGES[status]}
      </Typography>
    </Stack>
  );
}

/** True while the nearest provider reports a lost connection; false outside a provider. */
export function useConnectionLost(): boolean {
  return useContext(ConnectionContext).status === 'offline';
}

export function ConnectionStatusProvider({ children }: PropsWithChildren): React.JSX.Element {
  const status = useConnectionBannerStatus(useAppSelector(getIsOffline));
  const [footerCount, setFooterCount] = useState(0);

  const registerFooter = useCallback((): (() => void) => {
    setFooterCount((count) => count + 1);
    return () => setFooterCount((count) => count - 1);
  }, []);
  const value = useMemo<ConnectionContextValue>(() => ({ status, registerFooter }), [status, registerFooter]);

  return (
    <ConnectionContext.Provider value={value}>
      {children}
      {/* One persistent live region announces each change once, however many banners render. */}
      <Box role='status' sx={visuallyHidden}>
        {status ? CONNECTION_BANNER_MESSAGES[status] : null}
      </Box>
      <Fade in={Boolean(status) && footerCount === 0} unmountOnExit>
        <Box sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: (theme) => theme.zIndex.snackbar }}>
          {/* Status only clears after `reconnected`, so the exit transition keeps showing it. */}
          <ConnectionBanner status={status ?? 'reconnected'} />
        </Box>
      </Fade>
    </ConnectionContext.Provider>
  );
}

type ConnectionStatusFooterProps = PropsWithChildren<{
  /** Show the banner above the actions and keep them usable, for actions that work offline. */
  keepActions?: boolean;
}>;

/** Dialog footer that shows the connection banner, replacing the actions unless `keepActions` is set. */
export function ConnectionStatusFooter({
  children,
  keepActions = false,
}: ConnectionStatusFooterProps): React.JSX.Element {
  const { status, registerFooter } = useContext(ConnectionContext);
  const [transitionActive, setTransitionActive] = useState(false);
  useEffect(registerFooter, [registerFooter]);

  const banner = (
    <Fade
      in={Boolean(status)}
      onEnter={() => setTransitionActive(true)}
      onExited={() => setTransitionActive(false)}
      unmountOnExit
    >
      <Box>
        <ConnectionBanner status={status ?? 'reconnected'} variant={keepActions ? 'bar' : 'footer'} />
      </Box>
    </Fade>
  );

  if (keepActions) {
    return (
      <Box sx={FOOTER_SX}>
        {banner}
        {children}
      </Box>
    );
  }

  const replacesActions = Boolean(status) || transitionActive;
  return (
    <Box sx={FOOTER_SX}>
      {/* A disabled fieldset keeps the actions mounted but blocks clicks and implicit form submission. */}
      <Box
        component='fieldset'
        disabled={replacesActions}
        sx={{ border: 0, m: 0, p: 0, minWidth: 0, display: replacesActions ? 'none' : undefined }}
      >
        {children}
      </Box>
      {banner}
    </Box>
  );
}
